import * as Sentry from '@sentry/react';
import { configuration } from '../../../configuration';
import { safeRedirect } from '../../../utils/helpers/common';

/**
 * Where a member should land once the OAuth round-trip is done (ADR-CM-037).
 *
 * Two shapes, deliberately kept apart: an `external` value is an allowlisted
 * absolute URL fed to `window.location`, an `internal` value is a same-origin
 * path fed to react-router. Collapsing them would let an absolute URL be
 * treated as a router path (or the reverse), which is how an open redirect
 * gets in.
 */
export type PostAuthDestination =
  { kind: 'external'; value: string } | { kind: 'internal'; value: string };

/** Same-origin relative path, consumed by react-router in `LoginCallback`. */
export const POST_AUTH_REDIRECT_KEY = 'sr-post-auth-redirect';

/**
 * Cross-app return URL (e.g. MSR2 -> MSR3 -> MSR2). Kept separate from
 * POST_AUTH_REDIRECT_KEY because that one holds same-origin paths fed
 * to react-router; this one holds an absolute URL fed to window.location.
 *
 * As of ADR-CM-037 this is the *fallback* path -- the destination now rides
 * the OAuth `state` parameter. Kept for one release so the deploy cannot
 * regress the flow that works today.
 */
export const POST_AUTH_EXTERNAL_REDIRECT_KEY = 'sr-post-auth-external-redirect';

/**
 * Destination recovered from the OAuth `state` round-trip, parked until
 * something is in a position to honour it. Written by the bootstrap Hub
 * listener; drained by `MfaSetupGate` (external) and `LoginCallback`
 * (internal).
 */
const PARKED_DESTINATION_KEY = 'sr-post-auth-parked-destination';

/**
 * Breadcrumb of the write, so the Sentry report at the `/dashboard` fallback
 * can tell "the destination was never recorded" from "it was recorded and
 * lost". Without it that ambiguity survives this fix (APP-2364).
 */
const DESTINATION_WRITTEN_MARKER_KEY = 'sr-post-auth-destination-written';

/** Last `signInWithRedirect_failure` seen on the Hub, for the error screen. */
const OAUTH_FAILURE_KEY = 'sr-oauth-failure';

function newPageLoadId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // No `crypto`, or no `randomUUID` outside a secure context.
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Identifies the current page load. A recorded failure is only relevant to
 * the load that recorded it: Amplify completes the OAuth exchange during
 * `Amplify.configure()`, so anything stamped by an earlier navigation in the
 * same tab must not resurface (e.g. as a generic error page an hour later
 * when the session expires).
 *
 * A per-load nonce rather than a `Date.now()` comparison: the wall clock can
 * step backwards (NTP correction, manual change, VM resume) between this
 * module being evaluated and the failure being recorded, and a `recordedAt <
 * loadedAt` test would then discard a genuine same-load failure -- restoring
 * the silent restart this mechanism exists to stop (APP-2364). Equality
 * against an id minted once per load cannot be fooled that way.
 */
const PAGE_LOAD_ID = newPageLoadId();

function readSession(key: string): null | string {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // best-effort
  }
}

function clearSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // best-effort
  }
}

/**
 * Validate a raw return target into a destination, or `null`.
 *
 * Runs on every read, not just the write, because the value round-trips
 * through the browser -- the OAuth `state` parameter is no more trustworthy
 * than the sessionStorage entry it replaces.
 */
export function parseDestination(
  raw: null | string | undefined,
): PostAuthDestination | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  if (/^https?:\/\//i.test(raw.replace(/\\/g, '/').trim())) {
    const validated = safeRedirect(raw, '/', [
      configuration.MYSCREENRIGHTS_BASE,
    ]);

    return validated === '/' ? null : { kind: 'external', value: validated };
  }

  const validated = safeRedirect(raw, '/');

  return validated === '/' ? null : { kind: 'internal', value: validated };
}

/**
 * The string handed to `signInWithRedirect({ customState })`. Amplify encodes
 * it into the OAuth `state` parameter and hands it back via the Hub, so it
 * survives a tab discontinuity that would lose a sessionStorage entry.
 */
export function toCustomState(destination: PostAuthDestination): string {
  return destination.value;
}

/** Record that a destination was handed to the OAuth round-trip. */
export function markDestinationWritten(destination: PostAuthDestination): void {
  writeSession(
    DESTINATION_WRITTEN_MARKER_KEY,
    JSON.stringify({ at: Date.now(), kind: destination.kind }),
  );
  Sentry.addBreadcrumb({
    category: 'auth',
    data: { kind: destination.kind },
    level: 'info',
    message: 'post-auth destination handed to OAuth state',
  });
}

export function readDestinationWrittenMarker(): {
  at?: number;
  kind?: string;
} | null {
  const raw = readSession(DESTINATION_WRITTEN_MARKER_KEY);

  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearDestinationWrittenMarker(): void {
  clearSession(DESTINATION_WRITTEN_MARKER_KEY);
}

/** Park a destination recovered from the OAuth round-trip. */
export function parkDestination(destination: PostAuthDestination): void {
  writeSession(PARKED_DESTINATION_KEY, JSON.stringify(destination));
}

/** Read the parked destination without consuming it. */
export function peekParkedDestination(): PostAuthDestination | null {
  const raw = readSession(PARKED_DESTINATION_KEY);

  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);

    if (parsed?.kind !== 'external' && parsed?.kind !== 'internal') return null;
    // Re-validate rather than trusting what we parked: sessionStorage is
    // writable by anything running on this origin.
    const destination = parseDestination(parsed.value);

    return destination?.kind === parsed.kind ? destination : null;
  } catch {
    return null;
  }
}

/**
 * Take the parked destination if it matches `kind`, consuming it.
 *
 * Split by kind because the two drain points sit at different layers:
 * `MfaSetupGate` renders outside the router and can only do a full-page
 * `window.location` hand-off, while `LoginCallback` is inside it and should
 * `navigate()` rather than reload the app.
 */
export function takeParkedDestination(
  kind?: PostAuthDestination['kind'],
): PostAuthDestination | null {
  const destination = peekParkedDestination();

  if (!destination) {
    // Drop anything unparseable so a junk entry cannot outlive the flow.
    clearSession(PARKED_DESTINATION_KEY);

    return null;
  }
  if (kind && destination.kind !== kind) return null;
  clearSession(PARKED_DESTINATION_KEY);

  return destination;
}

export function clearParkedDestination(): void {
  clearSession(PARKED_DESTINATION_KEY);
}

/**
 * Drop both pre-ADR-CM-037 fallback keys.
 *
 * One function rather than a pair of `removeItem` calls repeated at each
 * terminal branch: `MfaSetupGate` cleared neither and so left both behind on
 * every completed cross-app round trip, because the only place that did clear
 * them -- `LoginCallback` -- is lazy and never mounts on that path. The keys
 * outlive the flow in that tab and steal the *next* sign-in. Any branch that
 * ends a round trip owes both of them, not whichever one it happened to read.
 */
export function clearFallbackDestinations(): void {
  clearSession(POST_AUTH_EXTERNAL_REDIRECT_KEY);
  clearSession(POST_AUTH_REDIRECT_KEY);
}

/**
 * Drop a cross-app destination left behind by an earlier attempt in this tab.
 *
 * Called as a sign-in flow starts that has no cross-app destination of its
 * own. `POST_AUTH_EXTERNAL_REDIRECT_KEY` is only ever written at the moment a
 * flow begins, so anything still sitting there belongs to an attempt that
 * never reached a terminal branch -- browser Back out of the Hosted UI being
 * the ordinary way. Left in place it outranks the same-origin path in
 * `LoginCallback` and sends a member who asked for an MSR3 deep link to MSR2
 * instead (APP-2364 QA, case 4).
 *
 * Only the external key. The same-origin key is written by `ManagedLoginGate`
 * immediately before this runs and holds the destination we are trying to
 * honour -- clearing it here would break the very path being rescued.
 *
 * The written marker is deliberately left alone: the abandoned flow really did
 * record a destination and really did lose it, and that marker is the only
 * evidence of it. Dropping it here would erase a true "recorded then lost"
 * signal rather than a false one.
 */
export function clearStaleExternalDestination(): void {
  clearSession(POST_AUTH_EXTERNAL_REDIRECT_KEY);
}

export type OAuthFailure = { message?: string; name?: string };

/**
 * Whether the current URL is an OAuth provider response, i.e. carries either
 * the `code`+`state` pair of a genuine callback or a provider-reported
 * `error`.
 *
 * Amplify raises `signInWithRedirect_failure` for an *abandoned* flow as well
 * as a failed one -- `completeOAuthFlow` throws 'User cancelled OAuth flow.'
 * when `code` or `state` is missing, and `validateState` throws
 * '`signInWithRedirect` has been canceled.' when the saved state is gone --
 * both of which happen on a bare page load, e.g. pressing browser Back out of
 * the Hosted UI. Those must keep restarting SSO, not render an error page.
 */
export function isOAuthRedirectResponse(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);

  if (params.get('error')) return true;

  return Boolean(params.get('code') && params.get('state'));
}

/**
 * Record a Hub `signInWithRedirect_failure`.
 *
 * Amplify reports these on the Hub only -- nothing lands on the URL -- so
 * without this the gate's `getOAuthError()` sees a clean `/login-callback`
 * and silently restarts the flow (APP-2364).
 */
export function recordOAuthFailure(failure: OAuthFailure): void {
  writeSession(
    OAUTH_FAILURE_KEY,
    JSON.stringify({ ...failure, load: PAGE_LOAD_ID }),
  );
}

/**
 * Read the recorded failure *without* consuming it.
 *
 * Non-destructive on purpose: the only caller is `ManagedLoginGate`, which
 * reads during render, and that render runs again on every Authenticator
 * state transition. A destructive read there loses the failure on the second
 * render and drops the gate straight back into `signInWithRedirect()` -- the
 * silent restart this whole mechanism exists to stop (APP-2364).
 *
 * Staleness is handled by the page-load stamp rather than by consuming:
 * anything recorded by a previous load in this tab is dropped.
 */
export function peekOAuthFailure(): OAuthFailure | null {
  const raw = readSession(OAUTH_FAILURE_KEY);

  if (!raw) return null;
  let parsed: (OAuthFailure & { load?: string }) | null = null;

  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (!parsed || parsed.load !== PAGE_LOAD_ID) {
    clearSession(OAUTH_FAILURE_KEY);

    return null;
  }

  return { message: parsed.message, name: parsed.name };
}

/** Drop any recorded failure, e.g. when a fresh sign-in is initiated. */
export function clearOAuthFailure(): void {
  clearSession(OAUTH_FAILURE_KEY);
}

/**
 * Reported to Sentry rather than the member: silently landing someone in the
 * wrong app is what produced APP-2364, and `console.error` (the previous
 * habit at both call sites) is invisible in production.
 */
export function reportUnhonouredDestination(extra: Record<string, unknown>) {
  const marker = readDestinationWrittenMarker();

  // Deliberately does *not* clear the marker: reporting is not the end of a
  // round trip. `PreLoginPage` reports on arrival, while a round trip started
  // in another tab may still be in flight, and clearing here would downgrade
  // that flow's own report to `destinationWasRecorded: false`. Each terminal
  // branch clears its own marker instead.
  Sentry.captureMessage('MSR3 post-auth return_to not honoured', {
    extra: {
      ...extra,
      // Present marker + no destination == recorded then lost. Absent marker
      // == never recorded. That is the question APP-2364 could not answer.
      destinationWasRecorded: marker !== null,
      recordedAt: marker?.at,
      recordedKind: marker?.kind,
    },
    level: 'warning',
  });
}

export function reportSignInRedirectFailure(
  err: unknown,
  extra: Record<string, unknown> = {},
) {
  Sentry.captureMessage('MSR3 signInWithRedirect failed', {
    extra: {
      ...extra,
      reason: err instanceof Error ? err.message : String(err),
    },
    level: 'error',
  });
}

/**
 * Report a `signInWithRedirect_failure` that came from a real provider
 * response, i.e. a code exchange that actually failed.
 *
 * `recordOAuthFailure` puts the failure in front of the member; this puts it
 * in front of us. A silent exchange failure is what produced APP-2364, and
 * without this we still could not say how often one happens.
 */
export function reportOAuthExchangeFailure(
  failure: OAuthFailure,
  extra: Record<string, unknown> = {},
) {
  Sentry.captureMessage('MSR3 OAuth code exchange failed', {
    extra: { ...extra, message: failure.message, name: failure.name },
    level: 'error',
  });
}

/**
 * Errors on which Amplify's `TokenOrchestrator` clears the stored tokens, so
 * the stored-session probe has done its job. Copied from
 * `isAuthenticationError` in @aws-amplify/auth 6.20.0; re-check on upgrade.
 */
const TOKEN_CLEARING_ERRORS = [
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'RefreshTokenReuseException',
];

export type SessionProbeOutcome = 'error' | 'read' | 'timeout';

/**
 * Warn only when the tokens survive the stored-session probe: the
 * Authenticator then retries (or joins) the refresh and can still sign the
 * member out. The daily dead-token case settles as `read`; token-clearing
 * errors and `NetworkError` stay a breadcrumb.
 */
export function reportSessionProbeOutcome(
  outcome: SessionProbeOutcome,
  extra: { errorName?: string; timeoutMs?: number } = {},
) {
  try {
    // A slow OAuth exchange on /login-callback can time out too; the path
    // lets those be told apart from stale sessions.
    const pathname =
      typeof window === 'undefined' ? undefined : window.location.pathname;

    Sentry.addBreadcrumb({
      category: 'auth',
      data: { outcome, pathname, ...extra },
      level: 'info',
      message: 'stored-session probe settled',
    });

    const tokensSurvived =
      outcome === 'timeout' ||
      (outcome === 'error' &&
        extra.errorName !== 'NetworkError' &&
        !TOKEN_CLEARING_ERRORS.some((name) =>
          extra.errorName?.startsWith(name),
        ));

    if (tokensSurvived) {
      Sentry.captureMessage('MSR3 stored-session probe fell back', {
        extra: { outcome, pathname, ...extra },
        level: 'warning',
      });
    }
  } catch {
    // Reporting must never turn a settled probe into a rejected one.
  }
}
